import {
  currencySuffix,
  formatDate as formatDateShared,
  formatMoney as formatMoneyShared,
  numberFormatter,
} from "@ckgo2/shared/utils";
import { uiLocale } from "@/i18n";

/**
 * Datum v jazyce, ve kterém operátor admin právě používá — „25. 7. 2026" v češtině,
 * „7/25/2026" v angličtině.
 *
 * Existuje proto, že sdílený `formatDate()` má výchozí locale `en-US` (spotřebovávají ho i
 * server a e-maily), takže každé volání v adminu musí locale dosadit. Ze sedmdesáti volání to
 * dvě dělala správně a zbytek tiskl americká data v české administraci. Wrapper to dosazuje
 * sám, takže na to nejde zapomenout; přímý import `formatDate` ze `@ckgo2/shared/utils`
 * v adminu hlídá `packages/shared/src/adminLocaleGuard.test.ts`.
 *
 * Locale se čte při každém volání (ne při importu) — přepínač jazyka sice stránku reloaduje,
 * ale modul se tak nespoléhá na pořadí importů vůči i18n inicializaci.
 */
export function formatDate(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
  locale: string = uiLocale(),
): string {
  return formatDateShared(date, options, locale);
}

/**
 * Částka v měně a jazyce, ve kterém operátor admin právě používá — „32 900 Kč" v češtině,
 * „32,900 Kč" v angličtině (mění se seskupení číslic, ne zápis měny).
 *
 * Systém prodává v korunách, ale admin dřív vykresloval `$` natvrdo na patnácti místech
 * (analytika, newsletter, ceny termínů) — dolar byl v kódu jen znak před číslem, ne měna.
 * Formátovač proto žije tady, ve stejné vrstvě jako `formatDate`: jedna cesta, `uiLocale()`
 * dosazený sám, a `packages/shared/src/adminLocaleGuard.test.ts` hlídá, že se dolarový
 * prefix nevrátí ani v šablonovém literálu, ani jako JSX text.
 *
 * Sazbu řetězce dělá SDÍLENÝ `formatMoney` — přesně jako `formatDate` výš. Vlastní
 * `Intl` se `style: "currency"` tu stál jen do chvíle, než se objevila nekorunová měna:
 * `cs-CZ` z něj tiskne „1 200 US$", zatímco web, doklady i newsletter píší „1 200 USD".
 * Jedna objednávka by tak měla v adminu jiný zápis částky než ve faktuře, kterou dostane
 * zákazník. Locale zůstává parametrem, takže se seskupení číslic pořád řídí přepínačem jazyka.
 */
export function formatMoney(
  amount: number,
  currency: string = "CZK",
  locale: string = uiLocale(),
): string {
  return formatMoneyShared(amount, currency, locale);
}

/**
 * Kompaktní ČÍSLO v jazyce adminu — cs „45 tis." / „1,2 mil.", en „45K" / „1.2M".
 *
 * Osy grafů (Recharts `tickFormatter`) mají nečíselné popisky sázet stejným jazykem jako
 * tooltip vedle nich: vlastní `${Math.round(v / 1000)}K` v `components/analytics/index.ts`
 * psal „45K" na osu a „45 000" do tooltipu o dva pixely vedle. Je to tatáž třída chyby jako
 * natvrdo psaný dolar u částek — anglický formát v české administraci.
 *
 * Bez `maximumFractionDigits`: u kompaktní notace si `Intl` desetinná místa řídí sám;
 * vynucená hodnota by z „45 tis." udělala „45,0 tis.". Formátovač je memoizovaný
 * (`numberFormatter`) — na ose se volá jednou na datový bod při každém renderu i hoveru.
 */
export function formatCompactNumber(value: number, locale: string = uiLocale()): string {
  return numberFormatter(locale, { notation: "compact" }).format(value || 0);
}

/**
 * Kompaktní varianta PENĚZ pro dlaždice a grafy — cs „45 tis. Kč" / „1,2 mil. Kč",
 * en „45K Kč".
 *
 * Nestojí na sdíleném `formatPriceCompact`: ten vrací holé „45 tis." **bez měny a jen česky**,
 * takže by anglickému adminovi tiskl češtinu a na produkci s nulami by chybějící měna prošla
 * nepovšimnutá až do první nenulové částky.
 *
 * Zápis měny řeší tentýž `currencySuffix` jako `formatMoney` výš — `Intl` se
 * `style: "currency"` tady stál do chvíle, než dorazí nekorunová částka: `cs-CZ` z něj
 * tiskne „1,2 tis. US$", zatímco doklady píší „USD". Kompaktní je jen ČÍSLO, měna je pořád táž.
 */
export function formatMoneyCompact(
  amount: number,
  currency: string = "CZK",
  locale: string = uiLocale(),
): string {
  return `${formatCompactNumber(amount, locale)} ${currencySuffix(currency)}`;
}
